import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Building,
  MapPin,
  Columns,
  Plus,
  Upload,
  Check,
  X,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { companyProfileApi, authHelpers } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';
import { ASSET_BASE_URL as ASSET_URL } from '../services/api';

function AddBillProfileStep2() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Receive data from step 1
  const step1Data = location.state?.formData || {};
  const step1UploadedFiles = location.state?.uploadedFiles || {};
  const step1ExistingImages = location.state?.existingImages || {};
  const existingProfileId = location.state?.profileId || null;
  const isEdit = location.state?.isEdit || false;

  const [step2Data, setStep2Data] = useState({
    locality: location.state?.step2Data?.locality || '',
    areaPinCode: location.state?.step2Data?.areaPinCode || '',
    district: location.state?.step2Data?.district || '',
    state: location.state?.step2Data?.state || '',
    instructionType: '',
    currentInstructionText: '',
    savedInstructions: location.state?.step2Data?.savedInstructions || []
  });

  // Force reset if no data passed
  useEffect(() => {
    if (!location.state?.step2Data) {
      setStep2Data(prev => ({
        ...prev,
        locality: '',
        areaPinCode: '',
        district: '',
        state: '',
        instructionType: '',
        currentInstructionText: '',
        savedInstructions: []
      }));
    }
  }, [location.state]);

  const [uploadedFiles, setUploadedFiles] = useState(step1UploadedFiles);

  // ✅ NEW: Track existing image URLs from backend (received from step 1)
  const BASE_IMG = `${ASSET_URL}/uploads/`;

const toFullUrl = (val) => {
  if (!val) return null;
  if (val.startsWith('http')) return val;
  return BASE_IMG + val;
};
 const [existingImages, setExistingImages] = useState(() => {
  const imgs = location.state?.existingImages || {};
  return {
    companyLogo: toFullUrl(imgs.companyLogo),
    signature:   toFullUrl(imgs.signature),
    upiScanner:  toFullUrl(imgs.upiScanner),
  };
});

  const [errors, setErrors] = useState({});
  const [nextInstructionId, setNextInstructionId] = useState(
    (location.state?.step2Data?.savedInstructions?.length || 0) + 1
  );
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(true);
  const [submitting, setSubmitting] = useState(false);
// ✅ REPLACE: existingImages useState initialization

  // Auto-hide notification
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => setShowNotification(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStep2Data(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleAddInstruction = () => {
    if (!step2Data.currentInstructionText.trim()) return;
    const newInstruction = {
      id: nextInstructionId,
      type: step2Data.instructionType,
      text: step2Data.currentInstructionText.trim()
    };
    setStep2Data(prev => ({
      ...prev,
      savedInstructions: [...prev.savedInstructions, newInstruction],
      currentInstructionText: ''
    }));
    setNextInstructionId(prev => prev + 1);
  };

  const handleRemoveInstruction = (id) => {
    setStep2Data(prev => ({
      ...prev,
      savedInstructions: prev.savedInstructions.filter(inst => inst.id !== id)
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddInstruction(); }
  };

  // ✅ FIXED: Append to DOM before click — prevents garbage collection
  const handleFileUpload = (field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/jpg';
    input.style.display = 'none';

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (
          file.type === 'image/png' ||
          file.type === 'image/jpeg' ||
          file.type === 'image/jpg'
        ) {
          setUploadedFiles(prev => ({ ...prev, [field]: file }));
          // ✅ Clear existing image since user uploaded a new one
          setExistingImages(prev => ({ ...prev, [field]: null }));
          if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
        } else {
          message.warning('Only PNG, JPG or JPEG files are accepted');
        }
      }
      if (document.body.contains(input)) document.body.removeChild(input);
    };

    input.oncancel = () => {
      if (document.body.contains(input)) document.body.removeChild(input);
    };

    document.body.appendChild(input);
    input.click();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!step2Data.locality.trim()) newErrors.locality = 'Locality is required';
    if (!step2Data.areaPinCode.trim()) newErrors.areaPinCode = 'Area pin code is required';
    if (!step2Data.district.trim()) newErrors.district = 'District is required';
    if (!step2Data.state.trim()) newErrors.state = 'State is required';
    // ✅ UPDATED: Check both new upload AND existing image
    if (!uploadedFiles.companyLogo && !existingImages.companyLogo) {
      newErrors.companyLogo = 'Company logo is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePrevious = () => {
    navigate('/billing/add-profile', {
      state: {
        isEdit,
        profile: existingProfileId ? { id: existingProfileId, ...step1Data } : null,
        formData: step1Data,
        uploadedFiles,
        existingImages,
        step2Data
      }
    });
  };
const extractFilename = (urlOrFilename) => {
  if (!urlOrFilename) return undefined;
  // Agar URL hai to last segment lo
  try {
    const parts = urlOrFilename.split('/');
    return parts[parts.length - 1] || undefined;
  } catch {
    return urlOrFilename;
  }
};
 const handleSubmit = async (e) => {
  if (e && e.preventDefault) e.preventDefault();
  if (!validateForm()) return;

  try {
    setSubmitting(true);
    setErrors({});

    const instructionNotes = step2Data.savedInstructions.map((inst, idx) => ({
      instructionType: inst.type,
      instruction: inst.text,
      displayOrder: idx + 1,
    }));

    const profileData = {
      companyName:   step1Data.companyName,
      name:          step1Data.name,
      contactNumber: step1Data.contactNumber,
      email:         step1Data.email,
      panNumber:     step1Data.panNumber,
      gstNumber:     step1Data.gstNumber,
      fssaiNumber:   step1Data.fssaiNumber,
      tagLine:       step1Data.tagLine,
      locality:      step2Data.locality,
      areaPinCode:   step2Data.areaPinCode,
      district:      step2Data.district,
      state:         step2Data.state,
      instructionNotes,
      status:        false,
      createdById:   authHelpers.getUserId(),

      // ✅ Existing image filenames bhejo agar user ne new file select nahi kiya
      // Backend inhe preserve karega
      companyLogoUrl:  !uploadedFiles.companyLogo  ? extractFilename(existingImages.companyLogo)  : undefined,
      signatureUrl:    !uploadedFiles.signature    ? extractFilename(existingImages.signature)    : undefined,
      upiScannerUrl:   !uploadedFiles.upiScanner   ? extractFilename(existingImages.upiScanner)  : undefined,
    };

    let profileId;

    if (existingProfileId) {
      await companyProfileApi.updateProfile(existingProfileId, profileData);
      profileId = existingProfileId;
    } else {
      const response = await companyProfileApi.createCompanyProfile(profileData);
      profileId = response.id;
    }

    // ✅ Only upload if NEW file selected — skip if using existing
    if (uploadedFiles.companyLogo) {
      try {
        await companyProfileApi.uploadLogo(profileId, uploadedFiles.companyLogo);
      } catch (err) {
        message.warning('Logo upload failed: ' + (err.message || 'Unknown error'));
      }
    }

    if (uploadedFiles.signature) {
      try {
        await companyProfileApi.uploadSignature(profileId, uploadedFiles.signature);
      } catch (err) {
        message.warning('Signature upload failed: ' + (err.message || 'Unknown error'));
      }
    }

    if (uploadedFiles.upiScanner) {
      try {
        await companyProfileApi.uploadUpiScanner(profileId, uploadedFiles.upiScanner);
      } catch (err) {
        message.warning('UPI Scanner upload failed: ' + (err.message || 'Unknown error'));
      }
    }

    setIsSuccess(true);
    setNotificationMessage(existingProfileId ? 'Profile updated successfully!' : 'Profile created successfully!');
    setShowNotification(true);

    setTimeout(() => navigate('/billing/self-profile'), 2000);

  } catch (error) {
    console.error('Submit error:', error);
    setIsSuccess(false);
    setNotificationMessage(error.message || 'Failed to save profile. Please try again.');
    setShowNotification(true);
    setErrors({ submit: error.message || 'An error occurred' });
  } finally {
    setSubmitting(false);
  }
};

  // ✅ UPDATED: Reusable upload box with existing image preview support
  const UploadBox = ({ field, label, required = false }) => {
    const hasNewFile = uploadedFiles[field];
    const hasExistingImage = existingImages[field];

    // Show new uploaded file
    if (hasNewFile) {
      return (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <div className="border-2 rounded-lg p-3 bg-white" style={{ borderColor: currentTheme.color }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                  <img
                    src={URL.createObjectURL(hasNewFile)}
                    alt={label}
                    className="w-full h-full object-cover"
                    onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{hasNewFile.name}</p>
                  <p className="text-xs text-gray-500">{(hasNewFile.size / 1024).toFixed(2)} KB</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                <button
                  onClick={() => handleFileUpload(field)}
                  className="text-xs font-medium px-2 py-1 rounded-md transition-colors"
                  style={{ color: currentTheme.color, backgroundColor: `${currentTheme.color}15` }}
                >
                  Change
                </button>
                <button
                  onClick={() => setUploadedFiles(prev => ({ ...prev, [field]: null }))}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
        </div>
      );
    }

    // ✅ Show existing image from backend (edit mode)
    if (hasExistingImage) {
      return (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <div className="border-2 rounded-lg p-3 bg-white" style={{ borderColor: currentTheme.color }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                  <img
                    src={hasExistingImage}
                    alt={label}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="w-full h-full bg-green-50 flex items-center justify-center">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M9 12l2 2 4-4" stroke="#459B7A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" stroke="#459B7A" stroke-width="2"/>
                          </svg>
                        </div>
                      `;
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {label} uploaded
                  </p>
                  <p className="text-xs text-green-600">Existing image</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                <button
                  onClick={() => handleFileUpload(field)}
                  className="text-xs font-medium px-2 py-1 rounded-md transition-colors"
                  style={{ color: currentTheme.color, backgroundColor: `${currentTheme.color}15` }}
                >
                  Change
                </button>
                <button
                  onClick={() => setExistingImages(prev => ({ ...prev, [field]: null }))}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
        </div>
      );
    }

    // Show upload placeholder
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div
          onClick={() => handleFileUpload(field)}
          className="border-2 border-dashed rounded-lg p-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
          style={{ borderColor: errors[field] ? '#EF4444' : '#D1D5DB' }}
        >
          <div className="flex flex-col items-center justify-center space-y-1.5">
            <Upload className="w-6 h-6 text-gray-400" />
            <p className="text-xs font-medium text-gray-700">Upload {label.toLowerCase()}</p>
            <p className="text-xs text-gray-500 text-center">Only PNG, JPG or JPEG files are accepted</p>
          </div>
        </div>
        {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-6 pb-[150px]">

      {/* Notification Popup */}
      {showNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-start justify-center z-50 pt-20">
          <div
            className="bg-white border-2 rounded-xl px-6 py-4 flex items-center gap-4 shadow-2xl min-w-[300px] max-w-[400px]"
            style={{ borderColor: isSuccess ? currentTheme.color : '#EF4444' }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: isSuccess ? currentTheme.color : '#EF4444' }}
            >
              {isSuccess
                ? <CheckCircle className="w-6 h-6 text-white" />
                : <X className="w-6 h-6 text-white" />
              }
            </div>
            <p className="text-base font-medium text-gray-800 flex-1">{notificationMessage}</p>
            <button
              onClick={() => setShowNotification(false)}
              className="w-7 h-7 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
            >
              <X className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handlePrevious}
          className="w-10 h-10 flex items-center justify-center bg-white rounded-full border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEdit ? 'Edit bill profile' : 'Add new bill profile'}
        </h1>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Left Column */}
        <div className="lg:col-span-2 space-y-4">

          {/* Address */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
            <h2 className="text-base font-bold text-gray-800 mb-3">Address</h2>
            <div className="space-y-3">

              {/* Locality */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Locality <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text" name="locality" value={step2Data.locality} onChange={handleChange}
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border-2 rounded-lg focus:outline-none text-sm transition-all"
                    style={{ borderColor: errors.locality ? '#EF4444' : step2Data.locality ? currentTheme.color : '#E5E7EB' }}
                  />
                </div>
                {errors.locality && <p className="text-red-500 text-xs mt-1">{errors.locality}</p>}
              </div>

              {/* Pin + District + State */}
              <div className="flex flex-wrap gap-4">
                {[
                  { name: 'areaPinCode', label: 'Area pin code', icon: MapPin },
                  { name: 'district', label: 'District', icon: Building },
                  { name: 'state', label: 'State', icon: Columns },
                ].map(({ name, label, icon: Icon }) => (
                  <div key={name} className="flex-1 min-w-[150px]">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {label} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text" name={name} value={step2Data[name]} onChange={handleChange}
                        className="w-full pl-9 pr-3 py-2 bg-gray-50 border-2 rounded-lg focus:outline-none text-sm transition-all"
                        style={{ borderColor: errors[name] ? '#EF4444' : step2Data[name] ? currentTheme.color : '#E5E7EB' }}
                      />
                    </div>
                    {errors[name] && <p className="text-red-500 text-xs mt-1">{errors[name]}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Instruction Notes */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
            <h2 className="text-base font-bold text-gray-800 mb-3">Instructions notes</h2>
            <div className="space-y-3">

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Instruction type</label>
                <input
                  type="text"
                  value={step2Data.instructionType}
                  onChange={e => setStep2Data(prev => ({ ...prev, instructionType: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-50 border-2 rounded-lg focus:outline-none text-sm transition-all"
                  style={{ borderColor: step2Data.instructionType ? currentTheme.color : '#E5E7EB' }}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Instruction</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Enter instruction"
                    value={step2Data.currentInstructionText}
                    onChange={e => setStep2Data(prev => ({ ...prev, currentInstructionText: e.target.value }))}
                    onKeyPress={handleKeyPress}
                    className="flex-1 px-3 py-2 bg-gray-50 border-2 rounded-lg focus:outline-none text-sm transition-all"
                    style={{ borderColor: step2Data.currentInstructionText ? currentTheme.color : '#E5E7EB' }}
                  />
                  <button
                    type="button"
                    onClick={handleAddInstruction}
                    disabled={!step2Data.currentInstructionText.trim()}
                    className="p-2 text-white rounded-lg transition-all flex-shrink-0 shadow-sm disabled:cursor-not-allowed"
                    style={{ backgroundColor: step2Data.currentInstructionText.trim() ? currentTheme.color : '#D1D5DB' }}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {step2Data.savedInstructions.length > 0 && (
                <div className="space-y-2 mt-2">
                  {step2Data.savedInstructions.map((instruction, index) => (
                    <div key={instruction.id} className="flex items-start justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-start gap-2 flex-1">
                        <span className="text-xs font-medium text-gray-600 mt-0.5">{index + 1}.</span>
                        <p className="text-sm text-gray-800">{instruction.text}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveInstruction(instruction.id)}
                        className="p-1.5 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => { if (step2Data.currentInstructionText.trim()) handleAddInstruction(); }}
                className="text-sm font-medium transition-colors"
                style={{ color: currentTheme.color }}
              >
                + Add more
              </button>
            </div>
          </div>

          {/* Previous Button */}
          <div className="flex justify-end gap-3">
            <button
              type="button" onClick={handlePrevious}
              className="px-5 py-2 bg-gray-400 hover:bg-gray-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Previous
            </button>
          </div>
        </div>

        {/* Right Column - Attachments */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
            <h2 className="text-base font-bold text-gray-800 mb-3">Attachment</h2>
            <div className="space-y-4">
              <UploadBox field="companyLogo" label="Company Logo" required />
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Tag line <span className="text-red-500">*</span>
                </label>
                <input
                  type="text" value={step1Data.tagLine || ''} readOnly
                  className="w-full px-3 py-2 bg-gray-50 border-2 rounded-lg text-sm"
                  style={{ borderColor: currentTheme.color }}
                />
              </div>
              <UploadBox field="signature" label="Signature" />
              <UploadBox field="upiScanner" label="UPI Scanner" />
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end mb-[150px] mr-[200px]">
        <button
          type="button" onClick={handleSubmit} disabled={submitting}
          className="flex items-center space-x-2 px-6 py-2.5 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all shadow-lg"
          style={{ backgroundColor: currentTheme.color }}
        >
          {submitting ? (
            <><Loader2 className="w-5 h-5 animate-spin" /><span>Submitting...</span></>
          ) : (
            <><Check className="w-5 h-5" /><span>Submit</span></>
          )}
        </button>
      </div>
    </div>
  );
}

export default AddBillProfileStep2;